import React from 'react';
import { Tabs, Tab, TabScrollButton } from '@mui/material';
import { isNumeric } from '../../lib/helpers/types';

export type SegmentedControlOption<T extends Record<string, number | string>> =
	{
		label: string;
		key: keyof T;
		value: T[keyof T];
	};

export type SegmentedControlProps<T extends Record<string, number | string>> = {
	idAndAssistanceProps?: ( value: T[keyof T] ) => {
		id: string;
		'aria-controls': string;
	};
	options: Partial<Record<keyof T, { label: string }>>;
	enumeration: T;
	value: T[keyof T];
	onChange: ( e: React.SyntheticEvent, newValue: T[keyof T] ) => void;
	className?: string;
	dataCy?: string;
};

export const SegmentedControl = <T extends Record<string, number | string>>( {
	options,
	value,
	enumeration,
	onChange,
	className,
	dataCy,
	idAndAssistanceProps = ( value ) => ( {
		id: `${ value.toString().toLowerCase().replace( / /g, '-' ) }`,
		'aria-controls': `${ value.toString().toLowerCase().replace( / /g, '-' ) }`,
	} ),
}: SegmentedControlProps<T> ) => {
	/* Sometimes the value is a number, like with `enum X { Key }`, sometimes it's string,
	like `enum X { Key = 'value' }`, but Tabs just wants a number, so we convert it */
	const sanitizedValue =
		typeof value === 'number'
			? value
			: isNumeric( value )
				? parseInt( value )
				: Object.entries( options ).findIndex( ( [ key ] ) => key === value );
	return (
		<Tabs
			value={ sanitizedValue }
			onChange={ ( e, newValue ) =>
				onChange(
					e,
					Object.entries( options )[ newValue ][ 0 ] as unknown as T[keyof T]
				)
			}
			className={ className }
			data-cy={ dataCy }
			scrollButtons='auto'
			allowScrollButtonsMobile
			slots={ {
				scrollButtons: TabScrollButton,
			} }
		>
			{ Object.entries( options ).map( ( [ key, value ], index ) =>
				value ? (
					<Tab
						key={ index }
						label={ value.label }
						data-cy={ `${ dataCy || 'tab' }-${ enumeration[ key as keyof T ]
							.toString()
							.toLowerCase()
							.replace( / /g, '-' ) }` }
						{ ...idAndAssistanceProps( enumeration[ key as keyof T ] ) }
					/>
				) : null
			) }
		</Tabs>
	);
};
